import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useGetMigrationByIDQuery, useGetMigrationStepsByIDQuery } from '@frontend/api-sync-app-on-prem-migrations';
import MigrationDetailView, { MigrationDetailTab } from '../../../../../pages/MigrationDetailView/MigrationDetailView';
import { useIntegrationsDashboardShallowStore } from '../../../../../store';
import { normalizeMigration, normalizeSteps } from '../../../../../utils/normalizeMigration';

const MigrationPage = () => {
  const { migration: migrationId } = Route.useParams();
  const [activeTab, setActiveTab] = useState<MigrationDetailTab>('current');

  const {
    data: migrationData,
    isFetching: isMigrationFetching,
    isError: isMigrationError,
  } = useGetMigrationByIDQuery({
    request: { migrationId },
  });

  const {
    data: currentStepsData,
    isFetching: isCurrentStepsFetching,
    isError: isCurrentStepsError,
  } = useGetMigrationStepsByIDQuery({
    request: { migrationId, includeHistory: false },
  });

  const {
    data: historyStepsData,
    isFetching: isHistoryStepsFetching,
    isError: isHistoryStepsError,
  } = useGetMigrationStepsByIDQuery({
    request: { migrationId, includeHistory: true },
    options: { enabled: activeTab === 'history' },
  });

  const migration = useMemo(() => (migrationData ? normalizeMigration(migrationData) : undefined), [migrationData]);

  // `GetMigrationByID` does not return `migratedAt`, so fall back to the value
  // cached in the Zustand store by the dashboard list page.
  const { migratedAtById } = useIntegrationsDashboardShallowStore('migratedAtById');
  const cachedMigratedAt = migratedAtById[migrationId];
  const migrationWithMigratedAt = useMemo(
    () =>
      migration && !migration.migratedAt && cachedMigratedAt
        ? { ...migration, migratedAt: cachedMigratedAt }
        : migration,
    [migration, cachedMigratedAt]
  );

  const currentSteps = useMemo(
    () => normalizeSteps(currentStepsData?.steps, migrationId),
    [currentStepsData, migrationId]
  );
  const historySteps = useMemo(
    () => normalizeSteps(historyStepsData?.steps, migrationId),
    [historyStepsData, migrationId]
  );

  return (
    <MigrationDetailView
      migrationId={migrationId}
      migration={migrationWithMigratedAt}
      steps={currentSteps}
      historySteps={historySteps}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isLoading={isMigrationFetching || isCurrentStepsFetching}
      isHistoryLoading={isHistoryStepsFetching}
      isError={isMigrationError || isCurrentStepsError}
      isHistoryError={isHistoryStepsError}
    />
  );
};

export const Route = createFileRoute('/_auth/migrationdashboard/migration/$migration')({
  component: MigrationPage,
});
